import { PricingRowVM } from './vetcor-pricing-row.model'

export class PricingRowFunctions {
   static hasMissingRationale(row: PricingRowVM): boolean {
      return row.newPrice && !row.rationale?.trim()
   }

   static hasInvalidNumber(row: PricingRowVM): boolean {
      return row.newPrice && !/^\d+(\.\d+)?$/.test(row.newPrice.toString())
   }

   static isValidPrice(value: any): boolean {
      const regex = /^\d+(\.\d+)?$/
      return regex.test(value?.toString())
   }
}

/*
import { PricingManagerClient } from 'src/app/shared/services/ppv.api'
import { PricingCalculator } from '../pricing-calculator'
import { Directive, EventEmitter, Output } from '@angular/core'
import { PricingRowVM } from './vetcor-pricing-row.model'

@Directive()
export abstract class PricingRowFunctions {
   abstract toastr: { error(msg: string): void }
   abstract pricingManagerClient: PricingManagerClient
   abstract showValidationModal: boolean
   abstract highlightedRows: any[]
   abstract captainHighlightedRows: any[]
   abstract systemFeeRows: any[]
   abstract products: any[]
   abstract linkedFeeRows: any[]
   abstract approvedRows: any[]
   abstract rejectedRows: any[]
   abstract isCaptain: boolean
   abstract filteredProducts: any[]
   abstract highlightedPrices: Map<number, number>
   abstract getHighlightedProducts(): any[]
   abstract captainHighlightedPrices: Map<number, number>
   abstract onRowSave(data: any): void
   @Output() highlightedRowsChange = new EventEmitter<any[]>()
   abstract passedScheduleDate: boolean
   abstract approvedRows_ra: any[]
   abstract rejectedRows_ra: any[]
   abstract requireApprovalRows: any[]
   abstract requireApprovalPrices: Map<number, number>

   

   getFormattedPct(value: number): string {
      return (value * 100).toFixed(2) + '%'
   }

   toggleEdit(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      product.isEditing = !product.isEditing
   }

   cancelEdit(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      const editablePriceField = this.isCaptain ? 'opsPrice' : 'newPrice'
      const editableRationaleField = this.isCaptain ? 'finalComment' : 'rationale'
      product[editablePriceField] = null
      product[editableRationaleField] = ''
      this.toggleEdit(product)
   }

   canShowFeePriceImpact(group, forOps: boolean, fee: any) {
      return PricingCalculator.canFeeHavePriceImpact(fee) && this.hasPriceImpact(group, forOps)
   }

   getFeePriceImpact(group: any, forOps: boolean, fee: any): number | null {
      if (!group.product) return
      return PricingCalculator.getFeePriceImpact(group.product, fee, forOps)
   }

   hasPriceImpact(group: any, forOps: boolean): boolean {
      return PricingCalculator.doesProductGroupHavePriceImpact(group, forOps)
   }

   getPriceImpact(group: any, forOps: boolean): number | null {
      if (!group.product) {
         return null
      }
      return PricingCalculator.getPriceImpact(group, forOps)
   }

   getPriceImpactPct(group: any, forOps: boolean): number | null {
      if (!group.product) {
         return null
      }
      const requestedImpact = this.getPriceImpact(group, forOps)
      return Math.round(((requestedImpact * 100) / group.product.l12MRevenue) * 100) / 100.0
   }

   findFirstInvalidInput(): HTMLInputElement | null {
      const editablePriceField = this.isCaptain ? 'opsPrice' : 'newPrice' // Dynamically select price field
      const editableReasonField = this.isCaptain ? 'finalComment' : 'rationale'

      let invalidInput: HTMLInputElement | null = null

      this.filteredProducts.some((group) => {
         // Check main product
         if (group.product[editablePriceField] && !group.product[editableReasonField].trim()) {
            this.toastr.error('Rationale is required to continue')
            invalidInput = document.querySelector(`input[name="${editableReasonField}-${group.product.id}"]`)
            return true // Exit early if found
         }

         // Check products array
         const invalidProduct = group.products?.find((product) => product[editablePriceField] && !product[editableReasonField]?.trim())
         const productOnlyNo = group.products?.find(
            (product) => product[editablePriceField] && !/^\d+(\.\d+)?$/.test(product[editablePriceField]?.toString())
         )
         if (invalidProduct) {
            this.toastr.error('Rationale is required to continue')
            invalidInput = document.querySelector(`input[name="${editableReasonField}-${invalidProduct.id}"]`)
            return true
         }
         if (productOnlyNo) {
            this.toastr.error('Incorrect format. Only use numbers.')
            invalidInput = document.querySelector(`input[name="${editablePriceField}-${productOnlyNo.id}"]`)
            return true
         }

         // Check quantity break array
         const invalidSubQb = group.subQbRows?.find((qb) => qb[editablePriceField] && !qb[editableReasonField]?.trim())
         const subQbOnlyNo = group.subQbRows?.find((qb) => qb[editablePriceField] && !/^\d+(\.\d+)?$/.test(qb[editablePriceField]?.toString()))
         if (invalidSubQb) {
            this.toastr.error('Rationale is required to continue')
            invalidInput = document.querySelector(`input[name="${editableReasonField}-${invalidSubQb.id}"]`)
            return true
         }
         if (subQbOnlyNo) {
            this.toastr.error('Incorrect format. Only use numbers.')
            invalidInput = document.querySelector(`input[name="${editablePriceField}-${subQbOnlyNo.id}"]`)
            return true
         }

         // Check subMinRows array
         const invalidSubMin = group.subMinRows?.find((subMin) => subMin[editablePriceField] && !subMin[editableReasonField]?.trim())
         const subMinOnlyNo = group.subMinRows?.find(
            (subMin) => subMin[editablePriceField] && !/^\d+(\.\d+)?$/.test(subMin[editablePriceField]?.toString())
         )
         if (invalidSubMin) {
            this.toastr.error('Rationale is required to continue')
            invalidInput = document.querySelector(`input[name="${editableReasonField}-${invalidSubMin.id}"]`)
            return true
         }
         if (subMinOnlyNo) {
            this.toastr.error('Incorrect format. Only use numbers.')
            invalidInput = document.querySelector(`input[name="${editablePriceField}-${subMinOnlyNo.id}"]`)
            return true
         }

         // Check subBundleRows array
         const invalidSubBundle = group.subBundleRows?.find((subBundle) => subBundle[editablePriceField] && !subBundle[editableReasonField]?.trim())
         const subBundleOnlyNo = group.subBundleRows?.find(
            (subBundle) => subBundle[editablePriceField] && !/^\d+(\.\d+)?$/.test(subBundle[editablePriceField]?.toString())
         )
         if (invalidSubBundle) {
            this.toastr.error('Rationale is required to continue')
            invalidInput = document.querySelector(`input[name="${editableReasonField}-${invalidSubBundle.id}"]`)
            return true
         }
         if (subBundleOnlyNo) {
            this.toastr.error('Incorrect format. Only use numbers.')
            invalidInput = document.querySelector(`input[name="${editablePriceField}-${subBundleOnlyNo.id}"]`)
            return true
         }

         // Check subFeeRows array
         const invalidSubFee = group.subFeeRows?.find((fee) => fee[editablePriceField] && !fee[editableReasonField]?.trim())
         const subFeeOnlyNo = group.subFeeRows?.find((fee) => fee[editablePriceField] && !/^\d+(\.\d+)?$/.test(fee[editablePriceField]?.toString()))
         if (invalidSubFee) {
            this.toastr.error('Rationale is required to continue')
            invalidInput = document.querySelector(`input[name="${editableReasonField}-${invalidSubFee.id}"]`)
            return true
         }
         if (subFeeOnlyNo) {
            this.toastr.error('Incorrect format. Only use numbers.')
            invalidInput = document.querySelector(`input[name="${editablePriceField}-${subFeeOnlyNo.id}"]`)
            return true
         }

         return false // Continue to next group if no invalid input found
      })

      return invalidInput
   }

   calculateSystemFeeTotal(subSystemRows: any[]): number {
      if (!subSystemRows || subSystemRows.length === 0) return 0
      return subSystemRows.reduce((sum, row) => sum + (row.currentPrice || 0), 0)
   }

   calculateTotalRequestedFee(subFeeRows: any[]): number {
      if (!subFeeRows) return 0
      return subFeeRows
         .filter((fee) => fee.recordType === 'PF')
         .reduce((sum, fee) => {
            const updatedPrice = this.currentHighlightedPrices.get(fee.id)

            const matchingHighlightedFee = this.systemFeeRows.find((highlighted) => highlighted?.feeTypeSKey === fee?.feeTypeSKey)

            const matchingLinkedFee = this.currentHighlightedArray.find(
               (linked) => linked?.productSKey && fee?.linkedProductSKey && linked?.productSKey === fee?.linkedProductSKey
            )

            const matchingHighlightedPrice =
               matchingHighlightedFee?.newPrice > 0
                  ? matchingHighlightedFee.newPrice
                  : matchingLinkedFee?.newPrice > 0
                  ? matchingLinkedFee.newPrice
                  : matchingHighlightedFee?.modelPrice
                  ? matchingHighlightedFee?.modelPrice
                  : 0

            return (
               sum +
               (updatedPrice !== undefined && updatedPrice !== 0
                  ? updatedPrice
                  : matchingHighlightedPrice !== undefined && matchingHighlightedPrice !== 0
                  ? matchingHighlightedPrice
                  : fee?.newPrice
                  ? fee?.newPrice
                  : fee?.modelPrice)
            )
         }, 0)
   }

   calculateTotalRequestedOpsFee(subFeeRows: any[]): number {
      if (!subFeeRows) return 0
      return subFeeRows
         .filter((fee) => fee.recordType === 'PF')
         .reduce((sum, fee) => {
            const updatedPrice = this.currentHighlightedPrices.get(fee.id)

            const matchingHighlightedFee = this.systemFeeRows.find((highlighted) => highlighted?.feeTypeSKey === fee?.feeTypeSKey)

            const matchingLinkedFee = this.currentHighlightedArray.find(
               (linked) => linked?.productSKey && fee?.linkedProductSKey && linked?.productSKey === fee?.linkedProductSKey
            )

            const matchingHighlightedPrice =
               matchingHighlightedFee?.opsPrice > 0
                  ? matchingHighlightedFee.opsPrice
                  : matchingLinkedFee?.opsPrice > 0
                  ? matchingLinkedFee.opsPrice
                  : matchingHighlightedFee?.modelPrice
                  ? matchingHighlightedFee?.modelPrice
                  : 0

            return (
               sum +
               (updatedPrice !== undefined
                  ? updatedPrice
                  : matchingHighlightedPrice !== undefined && matchingHighlightedPrice !== null
                  ? matchingHighlightedPrice
                  : (fee.opsPrice ?? fee.modelPrice) || 0)
            )
         }, 0)
   }

   calculateSystemRequestedFee(subSystemRows: any[]): number {
      if (!subSystemRows || subSystemRows.length === 0) {
         return 0
      }

      return subSystemRows
         .filter((row) => row.recordType === 'SF')
         .reduce((sum, row) => {
            const updatedPrice = this.currentHighlightedPrices.get(row.id)
            return sum + (updatedPrice !== undefined ? updatedPrice : (row.newPrice ?? row.modelPrice) || 0)
         }, 0)
   }

   calculateSystemRequestedOpsFee(subSystemRows: any[]): number {
      if (!subSystemRows || subSystemRows.length === 0) {
         return 0
      }

      return subSystemRows
         .filter((row) => row.recordType === 'SF')
         .reduce((sum, row) => {
            const updatedPrice = this.currentHighlightedPrices.get(row.id)
            return sum + (updatedPrice !== undefined ? updatedPrice : (row.opsPrice ?? row.modelPrice) || 0)
         }, 0)
   }

   calculateTotalRequestedPrice(group: any): number {
      return (
         (group.product.newPrice ?? group.product.modelPrice) +
         this.calculateTotalRequestedFee(group.subFeeRows) +
         this.calculateSystemRequestedFee(group.subSystemRows)
      )
   }

   calculateTotalOpsRequestedPrice(group: any): number {
      return (
         (group.product.opsPrice ?? group.product.modelPrice) +
         this.calculateTotalRequestedOpsFee(group.subFeeRows) +
         this.calculateSystemRequestedOpsFee(group.subSystemRows)
      )
   }

   populateProductModelFeesOnLoad(targetId: string, targetType: string): number | null {
      const feeRows = this.systemFeeRows || []
      const products = this.products || []
      const targetFeeGroup = feeRows.find((group) => group?.feeTypeSKey === targetId)
      const targetLinkedGroup = products.find((group) => group?.productSKey === targetId)
      const targetBundleGroup = products.find((group) => group?.productSKey === targetId && group?.recordType === 'P')

      if (targetFeeGroup && targetType === 'fee') {
         return targetFeeGroup.modelPrice
      } else if (targetLinkedGroup && targetType === 'linked') {
         return targetLinkedGroup.modelPrice
      } else if (targetBundleGroup && targetType === 'bundle') {
         return targetBundleGroup.modelPrice
      }

      return null
   }

   getUpdatedPriceForFeeRow(targetId: string, targetType: string, forOps: boolean): number | null {
      const feeRows = this.systemFeeRows || []
      const products = this.products || []
      const targetFeeGroup = feeRows.find((group) => group?.feeTypeSKey === targetId)
      const targetLinkedGroup = products.find((group) => group?.productSKey === targetId)

      if (targetFeeGroup && targetType == 'fee') {
         if (forOps) {
            return targetFeeGroup ? targetFeeGroup.opsPrice : targetFeeGroup.modelPrice
         } else {
            return targetFeeGroup ? targetFeeGroup.newPrice : targetFeeGroup.modelPrice
         }
      } else if (targetLinkedGroup && targetType == 'linked') {
         if (forOps) {
            return targetLinkedGroup ? targetLinkedGroup.opsPrice : targetLinkedGroup.modelPrice
         } else {
            return targetLinkedGroup ? targetLinkedGroup.newPrice : targetLinkedGroup.modelPrice
         }
      }

      return null
   }

   getUpdatedRationaleForFeeRow(targetId: string, targetType: string, forOps: boolean): string | null {
      const feeRows = this.systemFeeRows || []
      const products = this.products || []
      const targetFeeGroup = feeRows.find((group) => group?.feeTypeSKey === targetId)
      const targetLinkedGroup = products.find((group) => group?.productSKey === targetId)

      if (targetFeeGroup && targetType == 'fee') {
         if (forOps) {
            return targetFeeGroup ? targetFeeGroup.finalComment : ''
         } else {
            return targetFeeGroup ? targetFeeGroup.rationale : ''
         }
      } else if (targetLinkedGroup && targetType == 'linked') {
         if (forOps) {
            return targetLinkedGroup ? targetLinkedGroup.finalComment : ''
         } else {
            return targetLinkedGroup ? targetLinkedGroup.rationale : ''
         }
      }

      return null
   }

   getUpdatedRationaleForBundleRow(targetId: string, forOps: boolean): string | null {
      const targetGroup = this.getHighlightedProducts().find((group) => group.productSKey === targetId)
      if (targetGroup) {
         if (forOps) {
            return targetGroup ? targetGroup.finalComment : ''
         } else {
            return targetGroup ? targetGroup.rationale : ''
         }
      }
      return null
   }

   getUpdatedPriceForBundleRow(targetId: string, forOps: boolean): number | null {
      const targetGroup = this.getHighlightedProducts().find((group) => group.productSKey === targetId)
      if (targetGroup) {
         if (forOps) {
            return targetGroup ? targetGroup.opsPrice : targetGroup.modelPrice
         } else {
            return targetGroup ? targetGroup.newPrice : targetGroup.modelPrice
         }
      }
      return null
   }

   // Save and update methods
   onPriceSave(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }

      if (product.newPrice == 'NaN' || !this.isValidPrice(product.newPrice)) {
         this.toastr.error('Only numeric values allowed')
      } else if (product.newPrice <= 0) {
         this.toastr.error('Value must be greater than 0')
         // } else if (product.newPrice > (product.currentPrice * 2.0)) {
         //     this.toastr.error('Price change must be less than 100%');
      } else if (!product.rationale || product.rationale.trim().length <= 0) {
         this.toastr.error('Rationale is required to continue')
      } else if (product.rationale.trim().length <= 5) {
         this.toastr.error('Please provide a longer response')
      } else {
         if (product.newPrice > product.currentPrice * 2.0) {
            this.showValidationModal = true
         }

         this.pricingManagerClient.savePricingUpdate(product).subscribe({
            next: (data) => {
               if (!this.isCaptain) {
                  this.toggleEdit(product)
               }
               const exists = this.currentHighlightedArray.some((savedProduct) => savedProduct.id === product.id)
               if (!exists) {
                  this.currentHighlightedArray.push(product)
                  this.highlightedRowsChange.emit(this.currentHighlightedArray)
               }

               this.onRowSave(data)
            },
            error: () => {
               this.toastr.error('ERROR in communication with server. If the problem persists, please contact support!')
            },
         })
      }
   }

   onPriceSaveRemove(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      const toSave = { ...product, newPrice: null, rationale: '' }
      this.pricingManagerClient.savePricingUpdate(toSave).subscribe({
         next: () => {
            product.newPrice = null
            product.rationale = ''
            const exists = this.currentHighlightedArray.filter((productS) => productS.id !== product.id)
            this.highlightedRowsChange.emit(exists)
            this.onRowSave(product)
         },
         error: () => {
            this.toastr.error('ERROR in communication with server. If the problem persists, please contact support!')
         },
      })
   }

   onPriceSaveOps(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }

      if (product.opsPrice == 'NaN' || !this.isValidPrice(product.opsPrice)) {
         this.toastr.error('Only numeric values allowed')
      } else if (product.opsPrice <= 0) {
         this.toastr.error('Value must be greater than 0')
         // } else if (product.opsPrice > (product.currentPrice * 2.0)) {
         //     this.toastr.error('Price change must be less than 100%');
      } else if (!product.finalComment || product.finalComment.trim().length <= 0) {
         this.toastr.error('Rationale is required to continue')
      } else if (product.finalComment.trim().length <= 5) {
         this.toastr.error('Please provide a longer response')
      } else {
         if (product.opsPrice > product.currentPrice * 2.0) {
            this.showValidationModal = true
         }

         this.pricingManagerClient.overridePricingUpdateRequest(product).subscribe({
            next: (data) => {
               this.toggleEdit(product)
               const exists = this.currentHighlightedArray.some((savedProduct) => savedProduct.id === product.id)
               if (!exists) {
                  this.currentHighlightedArray.push(product)
               }

               this.onRowSave(data)
            },
            error: () => {
               this.toastr.error('ERROR in communication with server. If the problem persists, please contact support!')
            },
         })
      }
   }

   onPriceSaveRemoveOps(product, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      var toSave = {
         ...product,
         opsPrice: null,
         finalComment: '',
         captainApproved: 0,
      }
      this.pricingManagerClient.overridePricingUpdateRequest(toSave).subscribe({
         next: (data) => {
            product.opsPrice = null
            product.finalComment = ''
            product.captainApproved = 0
            product.zohoTicketId = ''
            console.log(product)

            this.captainHighlightedRows = this.captainHighlightedRows.filter((productS) => productS.id !== product.id)

            if (product.newPrice <= 0) {
               this.currentHighlightedArray.filter((productS) => productS.id !== product.id)
            }

            this.onRowSave(data)
         },
         error: () => {
            this.toastr.error('ERROR in communication with server. If the problem persists, please contact support!')
         },
      })
   }

   onPriceApprove(product: any): void {
      event.stopPropagation()
      var toSave = { ...product, opsPrice: product.newPrice }
      this.pricingManagerClient.approvePricingUpdateRequest(toSave).subscribe({
         next: (data) => {
            product.opsPrice = product.newPrice
            product.captainApproved = 1

            const exists = this.currentHighlightedArray.some((savedProduct) => savedProduct.id === product.id)
            if (!exists) {
               this.currentHighlightedArray.push(product)
            }

            this.onRowSave(data)
         },
         error: (error) => {
            this.toastr.error('ERROR in communication with server. If the problem persist please contact support!')
         },
      })
   }

   onPriceReject(product: any): void {
      event.stopPropagation()
      var toSave = {
         ...product,
         opsPrice: product.modelPrice,
      }
      this.pricingManagerClient.rejectPricingUpdateRequest(toSave).subscribe({
         next: (data) => {
            product.opsPrice = product.modelPrice
            product.captainApproved = 2

            const exists = this.currentHighlightedArray.some((savedProduct) => savedProduct.id === product.id)
            if (!exists) {
               this.currentHighlightedArray.push(product)
            }

            this.onRowSave(data)
         },
         error: (error) => {
            this.toastr.error('ERROR in communication with server. If the problem persist please contact support!')
         },
      })
   }
}
*/
